import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import Tooltip from '@material-ui/core/Tooltip';
import WarningRoundedIcon from '@material-ui/icons/WarningRounded';
import { useSelector } from 'react-redux';
import { Container } from '@chakra-ui/react';
import { Button, Text } from 'components/v4';
import AGGridMain from 'components/tables/AGGrid/AGGridMain';
import useColumnState from '../../../hooks/useColumnState';
import { LoadingIndicator } from '@/components/v4';
import OrganisationStatusCellRenderer from '../companies/cellRenderer/OrganisationStatusCellRenderer';
import CompanyStatuses from '../companies/utils/statuses';
import ActionMenu from 'components/presentational/ActionMenu';
import { EditIcon } from '@chakra-ui/icons';
import { MdImage } from 'react-icons/md';
import CopyCellRenderer from 'components/tables/AGGrid/CustomCells/CopyCellRenderer';

import useGetAllPartners from './v5/queries/useGetAllPartners';
import useGetPartnerByID from './v5/queries/useGetPartnerByID';
import PartnerEdit from './v5/PartnerEdit';
import PartnerBranding from './edit/PartnerBranding';
import walkthroughIds from '../walkthroughIds';

const PARTNER_MODES = {
    MAIN_TABLE: 'MainTable',
    ADD: 'Add',
    EDIT: 'Edit',
    APPROVAL: 'Approval',
    DELETE: 'Delete',
    CHANGE_LOGO: 'Change Logo',
    LOADING: 'Loading',
};

/*
 * Partner component when clicking Partner tab of the sidebar
 */
const Partners = () => {
    const { requiredScope, currentScope } = useSelector((state) => state.login);
    const { currentWholesaler, currentPartner, currentCompany } = useSelector(
        (state) => state.navigationLists,
    );
    const gridRef = useRef();

    const [mode, setMode] = useState(PARTNER_MODES.MAIN_TABLE);
    const [selectedPartnerId, setSelectedPartnerId] = useState(null);
    const [selectedPartnerData, setSelectedPartnerData] = useState(null);

    const { partners: partnerWalkthroughIds } = walkthroughIds;

    const [tableData, setTableData] = useState({
        columns: [],
        data: [],
    });

    const { isLoading, isRefetching, isFetching, refetch } =
        useGetAllPartners(setTableData);

    const resetToMainTable = useCallback(() => {
        setMode(PARTNER_MODES.MAIN_TABLE);
        setSelectedPartnerId(null);
        setSelectedPartnerData(null);
        refetch();
    }, [refetch]);

    // Fetch full partner data when in CHANGE_LOGO mode
    const { isLoading: isLoadingPartner, isFetching: isFetchingPartner } =
        useGetPartnerByID(
            selectedPartnerId,
            (data) => {
                setSelectedPartnerData(data);
            },
            resetToMainTable,
            mode === PARTNER_MODES.CHANGE_LOGO,
        );

    const switchMode = useCallback((newMode, partnerId = null) => {
        setMode(newMode);
        if (partnerId !== null) {
            setSelectedPartnerId(partnerId);
        }
    }, []);

    const handleAdd = useCallback(() => {
        switchMode(PARTNER_MODES.ADD);
    }, [switchMode]);

    const handleEdit = useCallback(
        (params) => {
            setSelectedPartnerId(params.data.id);
            switchMode(PARTNER_MODES.EDIT);
        },
        [switchMode],
    );

    const handleChangeLogo = useCallback(
        (params) => {
            setSelectedPartnerId(params.data.id);
            switchMode(PARTNER_MODES.CHANGE_LOGO);
        },
        [switchMode],
    );

    const DefaultPlanCellRenderer = useCallback((params) => {
        const planName = (() => {
            if (!params.value) return '';
            if (typeof params.value === 'string') return params.value;
            if (typeof params.value === 'object' && params.value.name) {
                return params.value.name;
            }
            return '';
        })();

        const leftAdornment = !params.value ? (
            <Tooltip title="No Default Partner Plan has been set. This partner will not be billed.">
                <span>
                    <WarningRoundedIcon style={{ color: '#ff9800' }} />
                </span>
            </Tooltip>
        ) : null;

        return (
            <CopyCellRenderer value={planName} leftAdornment={leftAdornment} />
        );
    }, []);

    const baseColDefs = useMemo(() => {
        return [
            {
                field: 'name',
                headerName: 'Partner Name',
                flex: 1,
                minWidth: 200,
                sortable: true,
                sort: 'asc',
            },
            {
                field: 'entityName',
                headerName: 'Legal Entity Name',
                hide: true,
                flex: 1,
                minWidth: 200,
                sortable: true,
            },
            {
                field: 'status',
                headerName: 'Status',
                width: 150,
                hide: true,
                cellRenderer: (params) => {
                    return (
                        <OrganisationStatusCellRenderer
                            value={params.data?.status}
                            statusMessage={params.data?.statusMessage}
                            noCircle={false}
                        />
                    );
                },
                cellStyle: { display: 'flex', alignItems: 'center' },
                sortable: true,
                filter: 'agSetColumnFilter',
                valueFormatter: (params) => {
                    return (
                        CompanyStatuses[params?.value]?.displayedName ||
                        'Unknown'
                    );
                },
                filterParams: {
                    cellRenderer: (params) => {
                        const isSelectedAll = params?.value === '(Select All)';
                        const statusLookup = CompanyStatuses[params?.value];
                        const displayName = statusLookup?.displayedName;
                        const additionalInfo =
                            params?.value !== 0 && statusLookup?.toolTip
                                ? ` (${statusLookup.toolTip})`
                                : '';
                        if (!isSelectedAll) {
                            return (
                                <Text isTruncated>
                                    {`${displayName}${additionalInfo}`}
                                </Text>
                            );
                        } else {
                            return '(Select All)';
                        }
                    },
                },
            },
            {
                field: 'hostHeader',
                headerName: 'Host Header',
                flex: 1,
                minWidth: 150,
            },
            {
                field: 'defaultPartnerPlan',
                headerName: 'Default Partner Plan',
                width: 200,
                cellRenderer: DefaultPlanCellRenderer,
                valueGetter: (params) => {
                    return (
                        params.data?.defaultPartnerPlan?.name ||
                        params.data?.defaultPartnerPlan
                    );
                },
            },
            {
                field: 'serviceCountries',
                headerName: 'Service Countries',
                hide: true,
            },
            {
                field: 'pServiceCountries',
                headerName: 'Partner Service Countries',
                hide: true,
            },
            {
                field: 'pAttribute1',
                headerName: 'Partner Attribute 1',
                hide: true,
            },
            {
                field: 'pAttribute2',
                headerName: 'Partner Attribute 2',
                hide: true,
            },
            {
                field: 'pAttribute3',
                headerName: 'Partner Attribute 3',
                hide: true,
            },
            {
                field: 'id',
                headerName: 'Partner ID',
                hide: true,
            },
            {
                lockPosition: 'right',
                headerName: 'Action',
                maxWidth: 100,
                filter: false,
                suppressMovable: true,
                lockVisible: true,
                suppressColumnsToolPanel: true,
                suppressHeaderMenuButton: true,
                suppressHeaderFilterButton: true,
                enableRowGroup: false,
                resizable: false,
                sortable: false,
                cellStyle: () => ({
                    display: 'flex',
                    alignItems: 'center',
                }),
                cellRenderer: (params) => {
                    const actionItems = [
                        {
                            label: 'Edit',
                            onClick: () => handleEdit(params),
                            icon: EditIcon,
                            dataAttributes: {
                                'data-walkthroughid': `${partnerWalkthroughIds.ufEditButton}/${params.data.id}`,
                            },
                        },
                        {
                            label: 'Branding',
                            onClick: () => handleChangeLogo(params),
                            icon: MdImage,
                        },
                    ];

                    return <ActionMenu actionItems={actionItems} />;
                },
            },
        ];
    }, [
        DefaultPlanCellRenderer,
        partnerWalkthroughIds.ufEditButton,
        handleEdit,
        handleChangeLogo,
    ]);

    const { columns: colDefs, onColumnVisible } = useColumnState(baseColDefs);

    const isFetchingData = isLoading || isRefetching || isFetching;

    // Build table data object with columns from hook
    const tableDataWithColumns = useMemo(
        () => ({
            columns: colDefs,
            data: tableData.data,
        }),
        [colDefs, tableData.data],
    );

    // Ensure partners are always loaded when in main table mode
    useEffect(() => {
        if (
            mode === PARTNER_MODES.MAIN_TABLE &&
            tableData.data.length === 0 &&
            !isLoading
        ) {
            refetch();
        }
    }, [mode, tableData.data.length, isLoading, refetch]);

    // Handle scope/wholesaler/partner/company changes - reset to main table and clear data
    // Only trigger when scope or wholesaler actually changes, not on every mode change
    const prevScopeRef = useRef(currentScope);
    const prevWholesalerRef = useRef(currentWholesaler);
    const prevPartnerRef = useRef(currentPartner);
    const prevCompanyRef = useRef(currentCompany);

    useEffect(() => {
        const scopeChanged = prevScopeRef.current !== currentScope;
        const wholesalerChanged =
            prevWholesalerRef.current !== currentWholesaler;
        const partnerChanged = prevPartnerRef.current !== currentPartner;
        const companyChanged = prevCompanyRef.current !== currentCompany;

        if (
            scopeChanged ||
            wholesalerChanged ||
            partnerChanged ||
            companyChanged
        ) {
            if (mode !== PARTNER_MODES.MAIN_TABLE) {
                setMode(PARTNER_MODES.MAIN_TABLE);
                setSelectedPartnerId(null);
                setSelectedPartnerData(null);
            }
            prevScopeRef.current = currentScope;
            prevWholesalerRef.current = currentWholesaler;
            prevPartnerRef.current = currentPartner;
            prevCompanyRef.current = currentCompany;
        }
        // React Query will automatically refetch due to queryKey change
    }, [currentScope, currentWholesaler, currentPartner, currentCompany, mode]);

    const mainTable = (
        <Container
            as="div"
            maxW="container.xl"
            px={0}
            aria-label="partners main table">
            <Text
                sx={{
                    fontSize: '20',
                    fontWeight: '500',
                    marginBottom: '15px',
                }}>
                Partners
            </Text>
            <AGGridMain
                gridRef={gridRef}
                tableData={tableDataWithColumns}
                isLoading={isFetchingData}
                refetch={refetch}
                onColumnVisible={onColumnVisible}
                overlayNoRowsTemplate="No Partners Found"
                titleName="Partners"
                extraComponents={
                    requiredScope >= 60 && (
                        <div style={{ paddingLeft: '20px' }}>
                            <Button
                                size="sm"
                                width="120px"
                                onClick={handleAdd}
                                data-walkthroughid={
                                    partnerWalkthroughIds.ufAddButton
                                }>
                                + Add Partner
                            </Button>
                        </div>
                    )
                }
            />
        </Container>
    );

    const modalContent = () => {
        switch (mode) {
            case PARTNER_MODES.MAIN_TABLE:
                return mainTable;
            case PARTNER_MODES.ADD:
                return (
                    <PartnerEdit
                        mode="Add"
                        resetToMainTable={resetToMainTable}
                        switchToEditMode={(partnerId) => {
                            setSelectedPartnerId(partnerId);
                            setMode(PARTNER_MODES.EDIT);
                        }}
                        selectedPartnerId={null}
                    />
                );
            case PARTNER_MODES.EDIT:
                return (
                    <PartnerEdit
                        mode="Edit"
                        resetToMainTable={resetToMainTable}
                        selectedPartnerId={selectedPartnerId}
                    />
                );
            case PARTNER_MODES.CHANGE_LOGO: {
                // Show loading while fetching full partner data
                if (
                    isLoadingPartner ||
                    isFetchingPartner ||
                    !selectedPartnerData
                ) {
                    return <LoadingIndicator />;
                }
                return (
                    <PartnerBranding
                        partner={selectedPartnerData}
                        onBack={resetToMainTable}
                    />
                );
            }
            case PARTNER_MODES.LOADING:
                return <LoadingIndicator />;
            default:
                return mainTable;
        }
    };

    return <>{modalContent()}</>;
};

export default Partners;
